import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Nav = styled(motion.nav)<{ $scrolled: boolean }>`
  position: fixed;
  inset: 0 0 auto;
  z-index: 1000;
  border-bottom: 1px solid ${props => props.$scrolled ? props.theme.colors.border : 'transparent'};
  background: ${props => props.$scrolled ? 'rgba(255, 255, 255, 0.86)' : 'rgba(255, 255, 255, 0.72)'};
  box-shadow: ${props => props.$scrolled ? '0 1px 0 rgba(0, 0, 0, 0.04)' : 'none'};
  backdrop-filter: saturate(180%) blur(20px);
  transition: background 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
`;

const NavContainer = styled.div`
  width: min(1180px, calc(100% - 3rem));
  height: 68px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 720px) {
    width: min(100% - 2rem, 1180px);
    height: 60px;
  }
`;

const Brand = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  border: 0;
  background: transparent;
  color: ${props => props.theme.colors.textPrimary};
  cursor: pointer;
`;

const BrandMark = styled.span`
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 0.68rem;
  background: ${props => props.theme.colors.textPrimary};
  color: #fff;
  font-family: ${props => props.theme.fonts.mono};
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: -0.04em;
`;

const BrandText = styled.span`
  display: flex;
  align-items: baseline;
  gap: 0.55rem;
  font-size: 1.05rem;
  font-weight: 750;
  letter-spacing: -0.015em;

  small {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 0.66rem;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
  }

  @media (max-width: 420px) {
    small { display: none; }
  }
`;

const DesktopLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 1.45rem;

  @media (max-width: 920px) {
    display: none;
  }
`;

const NavButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.82rem;
  font-weight: 650;
  cursor: pointer;
  transition: color 160ms ease;

  &:hover {
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const CodeLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.48rem 0.78rem;
  border: 0;
  border-radius: 999px;
  background: ${props => props.theme.colors.textPrimary};
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: #fff;
  }
`;

const MenuButton = styled.button`
  width: 38px;
  height: 38px;
  display: none;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 0.7rem;
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textPrimary};
  cursor: pointer;

  @media (max-width: 920px) {
    display: grid;
    place-items: center;
  }
`;

const MobileMenu = styled(motion.div)<{ $open: boolean }>`
  display: none;

  @media (max-width: 920px) {
    display: ${props => props.$open ? 'grid' : 'none'};
    grid-template-columns: repeat(2, 1fr);
    gap: 0.4rem;
    padding: 0.7rem 1rem 1rem;
    border-top: 1px solid ${props => props.theme.colors.border};
    background: rgba(255, 255, 255, 0.96);

    ${NavButton}, ${CodeLink} {
      min-height: 42px;
      justify-content: center;
      border: 1px solid ${props => props.theme.colors.border};
      border-radius: 0.75rem;
      background: ${props => props.theme.colors.surface};
    }

    ${CodeLink} {
      border-color: ${props => props.theme.colors.textPrimary};
      background: ${props => props.theme.colors.textPrimary};
      color: #fff;
    }
  }
`;

const navItems = [
  ['Demo', 'video-showcase'],
  ['Trajectories', 'demo'],
  ['Overview', 'abstract'],
  ['Threat model', 'threat-model'],
  ['Benchmark', 'dataset'],
  ['Evaluation', 'evaluation'],
] as const;

export const Navigation: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setOpen(false);
  };

  return (
    <Nav $scrolled={scrolled} initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}>
      <NavContainer>
        <Brand onClick={() => scrollTo('top')} aria-label="Back to top">
          <BrandMark>&gt;_</BrandMark>
          <BrandText>ADVCLI <small>OS misuse benchmark</small></BrandText>
        </Brand>

        <DesktopLinks>
          {navItems.map(([label, id]) => (
            <NavButton key={id} onClick={() => scrollTo(id)}>{label}</NavButton>
          ))}
          <CodeLink href="https://github.com/SaFo-Lab/AdvCLI" target="_blank" rel="noopener noreferrer">
            GitHub ↗
          </CodeLink>
        </DesktopLinks>

        <MenuButton onClick={() => setOpen(value => !value)} aria-label="Toggle navigation" aria-expanded={open}>
          {open ? '×' : '☰'}
        </MenuButton>
      </NavContainer>

      <MobileMenu $open={open} initial={false} animate={{ opacity: open ? 1 : 0 }}>
        {navItems.map(([label, id]) => (
          <NavButton key={id} onClick={() => scrollTo(id)}>{label}</NavButton>
        ))}
        <CodeLink href="https://github.com/SaFo-Lab/AdvCLI" target="_blank" rel="noopener noreferrer">
          GitHub ↗
        </CodeLink>
      </MobileMenu>
    </Nav>
  );
};
